import React, { useState, useCallback, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ZoomInIcon,
  ZoomOutIcon,
  ExternalLinkIcon,
} from 'lucide-react'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString()

interface PDFViewerProps {
  documentId: string
  currentPage: number
  onPageChange: (page: number) => void
}

export const PDFViewer = ({
  documentId,
  currentPage,
  onPageChange,
}: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [scale, setScale] = useState(1.4)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)

  // Fetch PDF URL when component mounts
  useEffect(() => {
    const fetchPdfUrl = async () => {
      try {
        const response = await fetch(`/api/documents/${documentId}/pdf-url`, {
          method: 'GET',
          credentials: 'include', // Include cookies for authentication
          headers: {
            'Content-Type': 'application/json',
          }
        })

        if (response.ok) {
          const data = await response.json()
          setPdfUrl(data.pdf_url)
        } else {
          console.error('Failed to fetch PDF URL:', response.status, await response.text())
        }
      } catch (error) {
        console.error('Error fetching PDF URL:', error)
      }
    }

    fetchPdfUrl()
  }, [documentId])

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    
    // If currentPage is set, scroll to it after a brief delay to ensure pages are rendered
    if (currentPage && currentPage > 1) {
      setTimeout(() => {
        scrollToPage(currentPage)
      }, 500)
    }
  }, [currentPage])

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error)
  }, [])

  const [pagesLoaded, setPagesLoaded] = useState<Set<number>>(new Set())
  
  const onPageLoadSuccess = useCallback((page: { pageNumber: number }) => {
    setPagesLoaded(prev => new Set(prev).add(page.pageNumber))
  }, [])

  const onPageLoadError = useCallback((error: Error) => {
    console.error('Page load error:', error)
  }, [])

  const openPdfInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank')
    }
  }

  const goToPrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
      scrollToPage(currentPage - 1)
    }
  }

  const goToNextPage = () => {
    if (numPages && currentPage < numPages) {
      onPageChange(currentPage + 1)
      scrollToPage(currentPage + 1)
    }
  }

  const scrollToPage = (pageNumber: number) => {
    console.log('scrollToPage called for page:', pageNumber)
    
    const tryScroll = () => {
      const pageElement = document.getElementById(`pdf-page-${pageNumber}`)
      console.log('Looking for element:', `pdf-page-${pageNumber}`, 'found:', !!pageElement)
      if (pageElement) {
        console.log('Scrolling to page', pageNumber)
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return true
      }
      return false
    }

    // Try immediately first
    if (tryScroll()) return

    // If page element doesn't exist yet, retry with delays
    console.log('Page element not found immediately, retrying with delays...')
    const retryIntervals = [100, 300, 500, 1000, 2000]
    retryIntervals.forEach((delay, index) => {
      setTimeout(() => {
        console.log(`Retry ${index + 1} after ${delay}ms`)
        if (tryScroll()) {
          // Clear remaining timeouts by checking if we succeeded
          return
        }
      }, delay)
    })
  }

  // Effect to scroll to current page when currentPage changes from external source
  React.useEffect(() => {
    if (currentPage && numPages) {
      scrollToPage(currentPage)
    }
  }, [currentPage, numPages])

  // Effect to scroll to current page when the target page finishes loading
  React.useEffect(() => {
    if (currentPage && pagesLoaded.has(currentPage)) {
      setTimeout(() => {
        scrollToPage(currentPage)
      }, 100)
    }
  }, [currentPage, pagesLoaded])

  // Add scroll listener to update current page based on scroll position
  React.useEffect(() => {
    const scrollContainer = document.querySelector('.pdf-scroll-container')
    if (!scrollContainer || !numPages) return

    let scrollTimeout: NodeJS.Timeout

    const handleScroll = () => {
      // Debounce scroll events to avoid too many updates
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        const containerRect = scrollContainer.getBoundingClientRect()
        
        // Find which page is most visible in the viewport center
        for (let i = 1; i <= numPages; i++) {
          const pageElement = document.getElementById(`pdf-page-${i}`)
          if (pageElement) {
            const rect = pageElement.getBoundingClientRect()
            
            // Check if the page center is close to the viewport center
            const pageCenterY = rect.top + rect.height / 2
            const viewportCenterY = containerRect.top + containerRect.height / 2
            
            // If page is in the center area of the viewport
            if (Math.abs(pageCenterY - viewportCenterY) < containerRect.height / 3) {
              if (i !== currentPage) {
                console.log('Auto-updating current page to:', i)
                onPageChange(i)
              }
              break
            }
          }
        }
      }, 100)
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      clearTimeout(scrollTimeout)
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [numPages, currentPage, onPageChange])

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3.0))
  }

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5))
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex flex-col">
      <div className="border-b border-gray-200 p-3 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div className="mx-2 text-sm text-gray-700 flex items-center">
            <span>Page </span>
            <input
              type="number"
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value, 10)
                if (page >= 1 && page <= (numPages || 1)) {
                  onPageChange(page)
                  scrollToPage(page)
                }
              }}
              min={1}
              max={numPages || 1}
              className="w-12 px-1 py-0 text-center bg-transparent border-none text-gray-700 focus:outline-none focus:bg-white focus:border focus:border-blue-300 focus:rounded"
            />
            <span> of {numPages || '?'}</span>
          </div>
          <button
            onClick={goToNextPage}
            disabled={!numPages || currentPage >= numPages}
            className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={zoomOut}
            className="p-1 rounded-full hover:bg-gray-100"
            title="Zoom Out"
          >
            <ZoomOutIcon className="h-5 w-5 text-gray-600" />
          </button>
          <span className="mx-2 text-sm text-gray-700">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="p-1 rounded-full hover:bg-gray-100"
            title="Zoom In"
          >
            <ZoomInIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div className="border-l border-gray-300 h-6 mx-2"></div>
          <button
            onClick={openPdfInNewTab}
            className="p-1 rounded-full hover:bg-gray-100"
            title="Open in New Tab"
          >
            <ExternalLinkIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-gray-100 p-4 pdf-scroll-container">
        <div className="flex flex-col items-center space-y-4">
          {pdfUrl && (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center h-96 w-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading PDF document...</p>
                </div>
              </div>
            }
            error={
              <div className="flex items-center justify-center h-96 w-full">
                <div className="text-red-500 text-center">
                  <p className="font-medium mb-2">Failed to load PDF</p>
                  <p className="text-sm text-gray-500 mb-4">
                    The document may not be available or there was a connection issue.
                  </p>
                  <button
                    onClick={openPdfInNewTab}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <ExternalLinkIcon className="h-4 w-4 mr-2" />
                    Try Opening in New Tab
                  </button>
                </div>
              </div>
            }
          >
            {numPages && Array.from(new Array(numPages), (_, index) => (
              <div
                key={`page_${index + 1}`}
                id={`pdf-page-${index + 1}`}
                className="mb-4 flex justify-center"
              >
                <div className="relative">
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    onLoadSuccess={onPageLoadSuccess}
                    onLoadError={onPageLoadError}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="shadow-lg border border-gray-300"
                  />
                  <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                    Page {index + 1}
                  </div>
                </div>
              </div>
            ))}
          </Document>
          )}
        </div>
      </div>
    </div>
  )
}